import { Vector3 } from 'three';

import type { HighlightManager } from '../../../../three-helpers/highlight';
import type { DebugManager, LightingManager } from '../../../managers';
import type { KitchenDebugData } from '../../../types';
import { DEFAULT_CAMERA_XZ_DISTANCE, DEFAULT_CAMERA_Y } from '../../types/bounds-types';
import type { SceneCameraDelegate } from '../scene-camera-delegate.class';

export type SetKitchenDebugDataParams = {
	data: KitchenDebugData;
	debugManager: DebugManager;
	lightingManager: LightingManager | null;
	highlightManager: HighlightManager | null;
	cameraDelegate: SceneCameraDelegate | null;
};

export const setKitchenDebugDataCfn = (params: SetKitchenDebugDataParams): void => {
	const { data, debugManager, lightingManager, highlightManager, cameraDelegate } = params;

	const { origin, size, left, right, defaultIslandX = 0 } = data;


	// Validate required data - skip if dimensions are invalid
	if (!size || !size.width || !size.height || !size.depth) {
		console.warn('setKitchenDebugData: Skipping debug visualization - invalid size data:', {
			origin,
			size,
			circleRadius: size?.width ? size.width / 2 : 0
		});
		return;
	}

	// Calculate circle parameters
	const circleCenterX = origin?.x ?? 0;
	const circleCenterZ = origin?.z ?? 0;
	const circleY = size.height; // Kitchen height
	const circleRadius = size.width / 2;

	// Additional validation for calculated values
	if (circleRadius <= 0 || !Number.isFinite(circleRadius)) {
		console.warn('setKitchenDebugData: Skipping debug visualization - invalid circle radius:', circleRadius);
		return;
	}

	// console.log('setKitchenDebugData: Created kitchen debug visualization', {
	// 	origin,
	// 	size,
	// 	circleRadius,
	// 	cameraPosition: { x: circleCenterX, y: 68, z: circleCenterZ + circleRadius * 1.5 }
	// });

	// Highlight circle parameters
	const highlightCircleRadius = circleRadius + 36;
	const highlightCircleY = circleY + 90;

	// Note: _defaultPresentationPoint is set later after camera angle is calculated

	// 1. Green circle (kitchen bounds)
	debugManager.addCircle({
		id: 'kitchen-circle',
		center: new Vector3(circleCenterX, circleY, circleCenterZ),
		radius: circleRadius,
		color: 0x00ff00 // Green
	});

	// 2. Yellow circle (highlight light path)
	debugManager.addCircle({
		id: 'highlight-circle',
		center: new Vector3(circleCenterX, highlightCircleY, circleCenterZ),
		radius: highlightCircleRadius,
		color: 0xffff00 // Yellow
	});

	// 3. Green center sphere (1" diameter)
	debugManager.addSphere({
		id: 'circle-center',
		position: new Vector3(circleCenterX, circleY, circleCenterZ),
		radius: 0.5,
		color: 0x00ff00,
		label: 'Circle Center'
	});

	// 4. Light green spheres (left/right X edges on circle)
	debugManager.addSphere({
		id: 'left-x-edge',
		position: new Vector3(circleCenterX - circleRadius, circleY, circleCenterZ),
		radius: 1,
		color: 0x90EE90, // Light green
		label: 'Left X Edge'
	});
	debugManager.addSphere({
		id: 'right-x-edge',
		position: new Vector3(circleCenterX + circleRadius, circleY, circleCenterZ),
		radius: 1,
		color: 0x90EE90, // Light green
		label: 'Right X Edge'
	});

	// 5. Dark green spheres (left/right Z edges projected on circle)
	let leftZ = circleCenterZ - circleRadius;
	let rightZ = circleCenterZ + circleRadius;

	if (left?.origin && left?.size) {
		leftZ = left.origin.z + (left.size.depth / 2);
	}
	if (right?.origin && right?.size) {
		rightZ = right.origin.z + (right.size.depth / 2);
	}

	// Project Z positions onto the circle
	const leftZDelta = leftZ - circleCenterZ;
	const leftXOffset = Math.sqrt(Math.max(0, circleRadius * circleRadius - leftZDelta * leftZDelta));
	debugManager.addSphere({
		id: 'left-z-edge',
		position: new Vector3(circleCenterX - leftXOffset, circleY, leftZ),
		radius: 1,
		color: 0x006400, // Dark green
		label: 'Left Z Edge'
	});

	const rightZDelta = rightZ - circleCenterZ;
	const rightXOffset = Math.sqrt(Math.max(0, circleRadius * circleRadius - rightZDelta * rightZDelta));
	debugManager.addSphere({
		id: 'right-z-edge',
		position: new Vector3(circleCenterX + rightXOffset, circleY, rightZ),
		radius: 1,
		color: 0x006400, // Dark green
		label: 'Right Z Edge'
	});

	// 6. Blue sphere (default island X position on circle)
	const xDelta = defaultIslandX - circleCenterX;
	const zOffset = Math.sqrt(Math.max(0, circleRadius * circleRadius - xDelta * xDelta));
	const defaultIslandZ = circleCenterZ - zOffset; // Behind the default island
	debugManager.addSphere({
		id: 'default-island-x',
		position: new Vector3(defaultIslandX, circleY, defaultIslandZ),
		radius: 1,
		color: 0x0000ff, // Blue
		label: 'Default Island X'
	});

	// 7. Red sphere (camera target / leg midpoint)
	// Calculate as midpoint between dark green spheres on same side as blue sphere
	const leftZPos = new Vector3(circleCenterX - leftXOffset, circleY, leftZ);
	const rightZPos = new Vector3(circleCenterX + rightXOffset, circleY, rightZ);
	const blueSpherePos = new Vector3(defaultIslandX, circleY, defaultIslandZ);

	const getAngle = (pos: Vector3): number => {
		const dx = pos.x - circleCenterX;
		const dz = pos.z - circleCenterZ;
		return Math.atan2(dz, dx);
	};

	const blueSphereAngle = getAngle(blueSpherePos);
	const leftZAngle = getAngle(leftZPos);
	const rightZAngle = getAngle(rightZPos);

	// Calculate midpoint angle (same logic as legacy)
	const normalizeAngle = (a: number): number => ((a % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
	const nBlue = normalizeAngle(blueSphereAngle);
	const nLeft = normalizeAngle(leftZAngle);
	const nRight = normalizeAngle(rightZAngle);

	const isAngleBetween = (angle: number, start: number, end: number): boolean => {
		if (start <= end) return angle >= start && angle <= end;
		return angle >= start || angle <= end;
	};

	let midpointAngle: number;
	const arc1Midpoint = (nLeft + nRight) / 2;
	const arc2Midpoint = normalizeAngle(arc1Midpoint + Math.PI);

	if (isAngleBetween(nBlue, Math.min(nLeft, nRight), Math.max(nLeft, nRight))) {
		midpointAngle = arc1Midpoint;
	} else {
		midpointAngle = arc2Midpoint;
	}

	const redSpherePosition = new Vector3(
		circleCenterX + Math.cos(midpointAngle) * circleRadius,
		circleY,
		circleCenterZ + Math.sin(midpointAngle) * circleRadius
	);

	debugManager.addSphere({
		id: 'camera-target',
		position: redSpherePosition,
		radius: 1,
		color: 0xff0000, // Red
		label: 'Camera Target'
	});

	// 8. Camera position and line to camera vector target (red sphere)
	// Use stored values from camera delegate so debug visualizes the actual vectors
	const cameraPosition = cameraDelegate?.defaultCameraPosition ?? new Vector3(
		circleCenterX + Math.cos(midpointAngle + Math.PI) * DEFAULT_CAMERA_XZ_DISTANCE,
		DEFAULT_CAMERA_Y,
		circleCenterZ + Math.sin(midpointAngle + Math.PI) * DEFAULT_CAMERA_XZ_DISTANCE
	);
	const vectorTarget = cameraDelegate?.cameraVectorTarget ?? redSpherePosition;

	// Add camera position sphere (cyan)
	debugManager.addSphere({
		id: 'camera-position',
		position: cameraPosition,
		radius: 2,
		color: 0x00ffff, // Cyan
		label: 'Camera Position'
	});

	// Red arc path from elevated source to camera vector target
	const arcPath = cameraDelegate?.autoZoomArcPath;
	if (arcPath && arcPath.length > 1) {
		debugManager.addPath({
			id: 'camera-to-target-arc',
			points: arcPath,
			color: 0xff0000 // Red
		});
	}

	// Store fallbacks if not already set by setSceneCenterFromKitchenData
	if (cameraDelegate && !cameraDelegate.defaultCameraPosition) {
		cameraDelegate.defaultCameraPosition = cameraPosition.clone();
		cameraDelegate.defaultCameraTarget = new Vector3(
			origin?.x ?? 0,
			-(origin?.y ?? 0),
			origin?.z ?? 0
		);
		cameraDelegate.cameraVectorTarget = redSpherePosition.clone();
	}

	// Presentation point and auto-zoom position (set by performAutoZoom)
	const azPosition = cameraDelegate?.autoZoomPosition;
	const presentationPt = cameraDelegate?.defaultPresentationPoint;
	if (azPosition && presentationPt) {
		const arcTangentNorm = cameraDelegate?.autoZoomArcTangent?.clone()
			.normalize() ??
			vectorTarget.clone().sub(azPosition)
				.normalize();

		// Orange cylinder at auto-zoom position on the arc
		debugManager.addCylinder({
			id: 'auto-zoom-position',
			position: azPosition,
			radius: 2,
			height: 4,
			color: 0xff8800, // Orange
			label: 'Auto-Zoom Position',
			axis: arcTangentNorm
		});

		// Orange line from auto-zoom position to presentation point
		debugManager.addLine({
			id: 'presentation-line',
			start: azPosition,
			end: presentationPt,
			color: 0xff8800 // Orange
		});

		// Yellow disc at presentation point
		debugManager.addDisc({
			id: 'default-presentation-point',
			position: presentationPt,
			radius: 3,
			color: 0xffff00, // Yellow
			label: 'Default Presentation Point',
			normal: arcTangentNorm
		});
	}

	// 9. Orange wireframe (full kitchen bounds)
	// BoxGeometry is center-origin, so offset Y by height/2 so the box bottom sits on the floor (Y=0)
	// origin.y is ignored for Y positioning since all kitchens sit on the floor
	if (size.width > 0 && size.height > 0 && size.depth > 0) {
		debugManager.addWireframe({
			id: 'kitchen-bounds',
			object: null as any, // Will create from dimensions
			color: 0xff8800, // Orange
			dimensions: { width: size.width, height: size.height, depth: size.depth },
			position: new Vector3(origin.x, size.height / 2, origin.z)
		});
	}

	// 10. Light blue wireframe (left kitchen section)
	if (left && left.size.width > 0 && left.size.height > 0 && left.size.depth > 0) {
		debugManager.addWireframe({
			id: 'kitchen-left-bounds',
			object: null as any,
			color: 0x87CEEB, // Light blue
			dimensions: { width: left.size.width, height: left.size.height, depth: left.size.depth },
			position: new Vector3(left.origin.x, left.size.height / 2, left.origin.z)
		});
	}

	// 11. Dark blue wireframe (right kitchen section)
	if (right && right.size.width > 0 && right.size.height > 0 && right.size.depth > 0) {
		debugManager.addWireframe({
			id: 'kitchen-right-bounds',
			object: null as any,
			color: 0x00008B, // Dark blue
			dimensions: { width: right.size.width, height: right.size.height, depth: right.size.depth },
			position: new Vector3(right.origin.x, right.size.height / 2, right.origin.z)
		});
	}

	// 12. Blue pivot sphere (kitchen origin)
	debugManager.addSphere({
		id: 'pivot-origin',
		position: new Vector3(origin.x, -origin.y, origin.z),
		radius: 1,
		color: 0x0000ff, // Blue
		label: 'Pivot Origin'
	});

	// Update highlight manager circle config
	if (highlightManager) {
		highlightManager.setCircle({
			centerX: circleCenterX,
			centerZ: circleCenterZ,
			y: highlightCircleY,
			radius: highlightCircleRadius
		});
	}

	// NOTE: Camera positioning was removed from this method.
	// The camera is positioned during scene composition via zoomToFit.
	// Debug visualization should NOT reposition the camera - that causes
	// unwanted camera movement after the scene is built.
	// The debug spheres (cyan camera position, red target) show where the
	// "optimal" position would be, but don't actually move the camera there.

	// Set directional light position and target based on kitchen geometry
	// Light source: point on yellow circle offset 45 deg from camera angle for angled shadows
	if (lightingManager) {
		// Light position: on yellow circle at camera angle - 45 deg offset
		const cameraAngleForLight = Math.atan2(cameraPosition.z - circleCenterZ, cameraPosition.x - circleCenterX);
		const lightAngle = cameraAngleForLight - Math.PI / 4;
		const lightPosition = new Vector3(
			circleCenterX + Math.cos(lightAngle) * highlightCircleRadius,
			highlightCircleY,
			circleCenterZ + Math.sin(lightAngle) * highlightCircleRadius
		);
		lightingManager.setDirectionalPosition(lightPosition);

		// Light target: kitchen center for even downward shadows on knobs/handles
		const lightTarget = new Vector3(circleCenterX, circleY / 2, circleCenterZ);
		lightingManager.setDirectionalTarget(lightTarget);

		// console.log('setKitchenDebugData: Updated directional light', {
		// position: { x: lightPosition.x, y: lightPosition.y, z: lightPosition.z },
		// target: { x: redSpherePosition.x, y: redSpherePosition.y, z: redSpherePosition.z }
		// });
	}

	// console.log('setKitchenDebugData: Created kitchen debug visualization', {
	// 	origin,
	// 	size,
	// 	circleRadius,
	// 	cameraPosition: { x: cameraPosition.x, y: cameraPosition.y, z: cameraPosition.z },
	// 	redSpherePosition: { x: redSpherePosition.x, y: redSpherePosition.y, z: redSpherePosition.z },
	// 	hasLeft: !!left,
	// 	hasRight: !!right
	// });
};
